import axios from 'axios'
import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios'

import { networkError, toYcError, YcError } from './errors'
import { getActingTenantId, markAuthLost } from './session'
import type { Envelope } from './types'

export interface TrackedConfig extends InternalAxiosRequestConfig {
  __retried?: boolean
  __skipAuth?: boolean
}

let authFailureHandler: ((e: YcError) => void) | null = null
let apiErrorHandler: ((e: YcError) => void) | null = null

/** 注册登录态彻底失效的回调（portal 里跳登录页）。 */
export function setAuthFailureHandler(fn: ((e: YcError) => void) | null): void {
  authFailureHandler = fn
}

/**
 * 注册通用业务错误回调：非 401 的信封错误（40301 未开通、40303 企业停用等）
 * 在此抛给宿主，由宿主决定落到哪个页面。设置后错误仍会继续向上抛。
 */
export function setApiErrorHandler(fn: ((e: YcError) => void) | null): void {
  apiErrorHandler = fn
}

export const http = axios.create({
  baseURL: '/iam/api',
  withCredentials: true,
  timeout: 30_000,
})

http.interceptors.request.use((config) => {
  const c = config as TrackedConfig
  const method = (c.method ?? 'get').toLowerCase()
  if (method !== 'get' && method !== 'head' && method !== 'options') {
    c.headers.set('X-Requested-With', 'XMLHttpRequest')
  }
  const tid = getActingTenantId()
  if (tid !== null) {
    c.headers.set('X-Tenant-Id', String(tid))
  }
  return c
})

export interface SimpleRequest {
  url: string
  method: string
  params?: Record<string, unknown>
  data?: unknown
  /** 覆盖实例默认前缀（/iam/api），用于访问子系统自己的后端。 */
  baseURL?: string
  __skipAuth?: boolean
  /** 非 JSON 响应才需要：blob 拿二进制（下载 CSV），text 拿纯文本。 */
  responseType?: 'blob' | 'text'
  headers?: Record<string, string>
  /** 覆盖实例默认超时（30 秒）。分钟级的长操作（演示数据灌入）必须单独放宽。 */
  timeout?: number
}

/** 单次请求的可选覆盖项。 */
export interface RequestOpts {
  /** 毫秒。不传则用实例默认的 30 秒。 */
  timeout?: number
}

/** 响应拦截器已解包信封，故实际拿到的是 data 而非 AxiosResponse。 */
async function request<T>(cfg: SimpleRequest): Promise<T> {
  return (await http.request(cfg as TrackedConfig)) as T
}

export const get = <T>(url: string, params?: Record<string, unknown>, opts?: RequestOpts): Promise<T> =>
  request<T>({ url, method: 'get', params, timeout: opts?.timeout })

export const post = <T>(url: string, data?: unknown, opts?: RequestOpts): Promise<T> =>
  request<T>({ url, method: 'post', data, timeout: opts?.timeout })

export const put = <T>(url: string, data?: unknown): Promise<T> =>
  request<T>({ url, method: 'put', data })

export const del = <T>(url: string): Promise<T> => request<T>({ url, method: 'delete' })

/**
 * getBlob 拉二进制响应（CSV 模板/导出）。
 * 信封解包器只认带 code 数字的对象，Blob 原样放行，故拦截器链路上是安全的。
 */
export const getBlob = (url: string, params?: Record<string, unknown>): Promise<Blob> =>
  request<Blob>({ url, method: 'get', params, responseType: 'blob' })

/** getText 拉纯文本响应；信封接口请用 get，不要用它。 */
export const getText = (url: string, params?: Record<string, unknown>): Promise<string> =>
  request<string>({ url, method: 'get', params, responseType: 'text' })

/**
 * postText 以纯文本为请求体（如 CSV 导入正文），响应仍是信封 JSON。
 * axios 按响应类型解析，与请求体编码无关，故拦截器照常解包。
 */
export const postText = <T>(url: string, data: string, contentType = 'text/csv; charset=utf-8'): Promise<T> =>
  request<T>({ url, method: 'post', data, headers: { 'Content-Type': contentType } })

export interface Client {
  get: <T>(url: string, params?: Record<string, unknown>) => Promise<T>
  post: <T>(url: string, data?: unknown) => Promise<T>
  put: <T>(url: string, data?: unknown) => Promise<T>
  del: <T>(url: string) => Promise<T>
  /** 二进制下载（CSV 模板、报表导出）；信封解包器对 Blob 原样放行。 */
  getBlob: (url: string, params?: Record<string, unknown>) => Promise<Blob>
  /** 纯文本响应；信封接口请用 get。 */
  getText: (url: string, params?: Record<string, unknown>) => Promise<string>
  /** 纯文本请求体（CSV 导入正文），响应仍是信封 JSON。 */
  postText: <T>(url: string, data: string, contentType?: string) => Promise<T>
}

/**
 * clientFor 返回绑定了路径前缀的请求方法集，供子系统访问自己的后端
 * （如 clientFor('/wms/api')）。登录态、CSRF 头、X-Tenant-Id、401 刷新、
 * 信封解包与默认客户端完全一致——同一个实例，只是换前缀；刷新仍打 /iam/api。
 */
export function clientFor(baseURL: string): Client {
  return {
    get: <T>(url: string, params?: Record<string, unknown>): Promise<T> =>
      request<T>({ url, method: 'get', params, baseURL }),
    post: <T>(url: string, data?: unknown): Promise<T> =>
      request<T>({ url, method: 'post', data, baseURL }),
    put: <T>(url: string, data?: unknown): Promise<T> =>
      request<T>({ url, method: 'put', data, baseURL }),
    del: <T>(url: string): Promise<T> => request<T>({ url, method: 'delete', baseURL }),
    getBlob: (url: string, params?: Record<string, unknown>): Promise<Blob> =>
      request<Blob>({ url, method: 'get', params, baseURL, responseType: 'blob' }),
    getText: (url: string, params?: Record<string, unknown>): Promise<string> =>
      request<string>({ url, method: 'get', params, baseURL, responseType: 'text' }),
    postText: <T>(url: string, data: string, contentType?: string): Promise<T> =>
      request<T>({
        url,
        method: 'post',
        data,
        baseURL,
        headers: { 'Content-Type': contentType ?? 'text/csv; charset=utf-8' },
      }),
  }
}

let refreshing: Promise<void> | null = null

function runRefresh(): Promise<void> {
  return request<void>({ url: '/session/refresh', method: 'post', __skipAuth: true }).then(
    () => undefined,
  )
}

/** 单飞刷新：并发 401 只发一次 refresh，其余请求共享同一结果。 */
export function refreshSession(): Promise<void> {
  if (!refreshing) {
    const p = runRefresh().finally(() => {
      refreshing = null
    })
    refreshing = p
  }
  return refreshing
}

export function isRefreshing(): boolean {
  return refreshing !== null
}

function failAuth(e: YcError): void {
  // 必须先置位再回调：宿主回调里会同步发起跳登录页的导航，
  // 那次导航又会拉 /me，没有这个标记就成死循环。
  markAuthLost()
  authFailureHandler?.(e)
}

/** 解包后的返回值不再是 AxiosResponse，故在此对 axios 的类型声明做一次显式收敛。 */
type UnwrappedFulfilled = (response: AxiosResponse<Envelope<unknown>>) => AxiosResponse

function unwrap(response: AxiosResponse<Envelope<unknown>>): unknown {
  const env = response.data
  if (!env || typeof env.code !== 'number') return response.data as unknown
  if (env.code === 0) return env.data
  throw toYcError(env, response.status)
}

http.interceptors.response.use(
  unwrap as unknown as UnwrappedFulfilled,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) throw error
    const config = error.config as TrackedConfig | undefined
    const status = error.response?.status ?? 0
    const body = error.response?.data

    if (status === 0 || !error.response) throw networkError(error)

    const yc = toYcError(body, status)
    if (yc.code === 401) return handleUnauthorized(yc, config)
    apiErrorHandler?.(yc)
    throw yc
  },
)

async function handleUnauthorized(e: YcError, config: TrackedConfig | undefined): Promise<unknown> {
  if (!config || config.__skipAuth || config.__retried) throw e
  config.__retried = true
  try {
    await refreshSession()
  } catch (refreshErr) {
    failAuth(refreshErr instanceof YcError ? refreshErr : e)
    throw refreshErr instanceof YcError ? refreshErr : e
  }
  return http.request(config)
}